// 현황·경고카드 #/board — 학생별 남은 클리닉 + 경고카드를 한 표에 (v0.3 Board.kt · SCREENS §1-8)
// compact 카드 1단 · medium 카드 2열 · expanded·large 표(첫 열 고정, 넘치면 data-scroll-x). 합계가 기준 이상이면 빨간 띠·알약
import { useState, type CSSProperties } from "react";
import { openItems, sessionOrder, upcoming, warnsOf } from "../data/select";
import { useView } from "../data/store";
import { St, WARN_TYPES, type Session, type Student } from "../data/types";
import { addDays, fmtDate, parseYmd, todayStr, ymd } from "../lib/date";
import { go, useWidthClass } from "../ui/hooks";
import { Icon } from "../ui/icons";
import { Empty, Pill, StatusPill, StudentName } from "../ui/parts";
import { Loading } from "./Dashboard";
import { openSheet } from "./sheets";

type Row = { st: Student; open: number; last?: Session; next?: Session; warns: Record<string, number>; total: number };
const PERIODS = ["이번 달", "최근 7일", "누적"] as const;
const SORTS = ["경고 많은 순", "남은 클리닉 순", "이름순"] as const;

export function Board() {
  const v = useView();
  const wc = useWidthClass();
  const [period, setPeriod] = useState<string>("이번 달");
  const [sort, setSort] = useState<string>("경고 많은 순");
  const [q, setQ] = useState("");
  if (!v.status.loaded) return <Loading />;
  const th = v.settings.warnThreshold;
  const td = todayStr();
  const d0 = parseYmd(td);
  const from = period === "이번 달" ? ymd(new Date(d0.getFullYear(), d0.getMonth(), 1)) : period === "최근 7일" ? addDays(td, -6) : "0000";
  let rows: Row[] = v.students.filter((s) => !q.trim() || s.name.includes(q.trim()) || s.school.includes(q.trim())).map((st) => {
    const ws = warnsOf(v, st.id).filter((w) => w.date >= from);
    const done = v.sessions.filter((s) => s.studentId === st.id && s.status !== St.PLAN).sort(sessionOrder);
    return {
      st, open: openItems(v, st.id).length, last: done[done.length - 1], next: upcoming(v, st.id)[0],
      warns: Object.fromEntries(WARN_TYPES.map((t) => [t, ws.filter((w) => w.type === t).length])), total: ws.length,
    };
  });
  rows = sort === "경고 많은 순" ? rows.sort((a, b) => b.total - a.total || b.open - a.open)
    : sort === "남은 클리닉 순" ? rows.sort((a, b) => b.open - a.open) : rows.sort((a, b) => a.st.name.localeCompare(b.st.name, "ko"));
  const table = wc === "expanded" || wc === "large";

  return (
    <div className="page board">
      <div className="toolbar">
        <label className="search grow">
          <Icon name="search" size={20} />
          <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="학생 검색" aria-label="학생 검색" />
        </label>
        <button type="button" className="btn" onClick={() => openSheet({ kind: "warn", studentId: null })}><Icon name="plus" size={18} /> 경고카드</button>
      </div>
      <div className="chips filters" role="group" aria-label="기간·정렬">
        {PERIODS.map((p) => <button key={p} type="button" className={"chip" + (period === p ? " on" : "")} aria-pressed={period === p} onClick={() => setPeriod(p)}>{p}</button>)}
        <span className="chip-sep" aria-hidden="true" />
        {SORTS.map((s) => <button key={s} type="button" className={"chip chip-quiet" + (sort === s ? " on" : "")} aria-pressed={sort === s} onClick={() => setSort(s)}>{s}</button>)}
      </div>
      <p className="muted small lead">경고카드 {th}장 이상은 빨간색 · 학생을 누르면 상세 · 클리닉 지각·노쇼는 당일 클리닉에서 자동 기록</p>
      {rows.length === 0 ? <Empty>해당하는 학생이 없습니다.</Empty> : table ? (
        <div className="table-wrap" data-scroll-x>
          <table className="tbl tbl-board">
            <thead>
              <tr><th className="sticky-col">학생</th><th>남은 클리닉</th><th>최근 클리닉</th><th>다음 일정</th>{WARN_TYPES.map((t) => <th key={t} className="num">{t}</th>)}<th className="num">합계</th><th><span className="sr-only">기록</span></th></tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const over = r.total >= th;
                return (
                  <tr key={r.st.id} className={over ? "over" : ""} style={{ "--accent": over ? "var(--color-danger)" : "transparent" } as CSSProperties}>
                    <td className="sticky-col"><a className="cell-link" href={`#/students/${encodeURIComponent(r.st.id)}`}><StudentName st={r.st} /></a></td>
                    <td className="num">{r.open ? <b className="accent">{r.open}</b> : <span className="ok">✓</span>}</td>
                    <td>{r.last ? <span className="stack"><StatusPill status={r.last.status} /><span className="muted small">{fmtDate(r.last.date)}</span></span> : <span className="muted">—</span>}</td>
                    <td className={"small nowrap" + (!r.next && r.open ? " warn-text" : "")}>{r.next ? `${fmtDate(r.next.date)} ${r.next.time}` : r.open ? "없음" : "—"}</td>
                    {WARN_TYPES.map((t) => <td key={t} className="num">{r.warns[t] ? <b className="late">{r.warns[t]}</b> : <span className="muted">·</span>}</td>)}
                    <td className="num">{r.total ? <Pill color={over ? "var(--color-danger)" : "var(--color-late)"} solid={over}>{r.total}</Pill> : <span className="muted">0</span>}</td>
                    <td><button type="button" className="btn btn-ghost btn-icon" aria-label={`${r.st.name} 경고카드 기록`} onClick={() => openSheet({ kind: "warn", studentId: r.st.id })}>＋</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={"card-grid " + (wc === "medium" ? "cg-medium" : "")}>
          {rows.map((r) => {
            const over = r.total >= th;
            return (
              <article key={r.st.id} className={"bcard" + (over ? " over" : "")}>
                <div className="bcard-top">
                  <a className="cell-link grow" href={`#/students/${encodeURIComponent(r.st.id)}`}><StudentName st={r.st} /></a>
                  <Pill color={r.open ? "var(--color-accent)" : "var(--color-success)"}>{r.open ? `남은 ${r.open}` : "클리닉 완료"}</Pill>
                  {r.total ? <Pill color={over ? "var(--color-danger)" : "var(--color-late)"} solid={over}>경고 {r.total}</Pill> : null}
                </div>
                <p className="small">
                  {WARN_TYPES.filter((t) => r.warns[t]).map((t) => <span key={t} className="late bold">{t} {r.warns[t]} </span>)}
                  {r.total === 0 ? <span className="muted">경고 없음</span> : null}
                </p>
                <p className="muted small">최근: {r.last ? `${fmtDate(r.last.date)} ${r.last.status}` : "—"} · 다음: {r.next ? `${fmtDate(r.next.date)} ${r.next.time}` : "없음"}</p>
                <div className="bcard-actions">
                  <button type="button" className="btn btn-ghost" onClick={() => openSheet({ kind: "warn", studentId: r.st.id })}>＋ 경고카드</button>
                  <button type="button" className="btn btn-ghost" onClick={() => go(`students/${encodeURIComponent(r.st.id)}`)}>상세</button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
